import axios from 'axios';
import type {
  LoginCodeRequest,
  VerifyCodeRequest,
  AuthResponse,
  HourmeterDetail,
  HourmeterGroup,
  SignatureRequest,
  SignatureResponse,
  SendResidentCodeRequest,
  SendResidentCodeResponse,
  VerifyResidentCodeRequest,
  VerifyResidentCodeResponse,
  RejectHourmeterRequest,
  RejectHourmeterResponse,
  HourmeterUpdateData,
  HourmeterUpdateResponse,
  CreateHourmeterOperatorRequest,
  CreateHourmeterOperatorResponse,
  CreateFuelDiscountRequest,
  FuelDiscountResponse,
  FuelDiscountGroup,
} from '../types';

interface ActiveDealInfo {
  dealId: number;
  lastDate?: Date | string | null;
  lastFinalHour?: number | string | null;
  haveAccessorie: boolean;
  hasMultipleResidents?: boolean;
  residents?: Array<{
    residentId: number;
    name: string;
    phone?: string;
    email?: string;
  }>;
  hourmeterDates?: string[];
}


const API_BASE_URL = (import.meta as any).env.VITE_API_BASE_URL || 
  (typeof window !== 'undefined' 
    ? window.location.hostname === 'localhost' 
      ? 'http://localhost:2000'
      : window.location.hostname.includes('stg-') 
        ? 'https://stg-retri-b90a7a43a76f.herokuapp.com'
        : 'https://retriapp.herokuapp.com'
    : 'http://localhost:2000');
const API_PREFIX = '/api/v2/platform/operation-dashboard';

const api = axios.create({
  baseURL: `${API_BASE_URL}${API_PREFIX}`,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor to add auth token if needed
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('auth_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor for error handling
api.interceptors.response.use(
  (response) => response,
  (error) => {
    // El 403 va junto al 401 a propósito: la API responde 403 cuando el request
    // llega SIN token, y 401 cuando el token existe pero no sirve. Los dos casos
    // significan lo mismo acá — hay que volver a entrar.
    //
    // Manejar solo el 401 dejaba atrapado al que tenía la sesión a medias
    // (`operator` guardado, `auth_token` no): veía "Error al cargar" para siempre,
    // sin que nada lo mandara a login. Fue lo que dejó a residentes sin abrir su
    // acta cuando esas rutas pasaron a exigir token.
    const status = error.response?.status;
    // El login entra por acá igual, y ahí un 401/403 significa "código malo", no
    // "sesión vencida": redirigir se comería el mensaje de error y recargaría la
    // pantalla en la que el usuario ya está.
    const url = error.config?.url || '';
    const esLogin = url.startsWith('/auth/');

    if (!esLogin && (status === 401 || status === 403)) {
      // Clear auth data on unauthorized
      localStorage.removeItem('auth_token');
      localStorage.removeItem('operator');
      // Con el destino en ?redirect= vuelve a donde estaba después de entrar,
      // que es el mismo contrato que usa ProtectedRoute.
      const target = window.location.pathname + window.location.search;
      const redirect = target && target !== '/'
        ? `?redirect=${encodeURIComponent(target)}`
        : '';
      window.location.href = `/login${redirect}`;
    }
    return Promise.reject(error);
  }
);

export const authService = {
  
  // Send OTP code to operator's phone
  sendLoginCode: async (data: LoginCodeRequest): Promise<AuthResponse> => {
    const response = await api.post('/auth/send-code', data);
    return response.data;
  },

  // Verify OTP code and authenticate operator
  verifyLoginCode: async (data: VerifyCodeRequest): Promise<AuthResponse> => {
    const response = await api.post('/auth/verify-code', data);
    return response.data;
  },

  // Quick access from WhatsApp link
  quickAccess: async (phone: string): Promise<AuthResponse> => {
    const response = await api.get(`/quick-access?phone=${encodeURIComponent(phone)}`);
    return response.data;
  },
};

export const hourmeterService = {
  // Get user's hourmeters (operator, supervisor, or resident)
  getOperatorHourmeters: async (userId: number, role: 'operator' | 'supervisor' | 'resident' = 'operator'): Promise<HourmeterGroup[]> => {
    // Use new unified API for operators, supervisors, and residents
    const response = await api.get(`/users/${userId}/hourmeters?role=${role}`);
    return response.data;
  },

  // Get hourmeter detail for receipt
  getHourmeterForReceipt: async (
    hourmeterId: number,
    operatorId: number,
    role: 'operator' | 'supervisor' | 'resident' = 'operator'
  ): Promise<HourmeterDetail> => {
    const response = await api.get(
      `/hourmeters/${hourmeterId}/receipt?operatorId=${operatorId}&role=${role}`
    );
    return response.data;
  },

  /**
   * PDF del recibo como object URL.
   *
   * La ruta dejó de ser pública: antes se armaba la URL a mano y se metía en un
   * `<embed>` o en `window.open`, sin sesión. Como los ids son correlativos,
   * cambiar el número devolvía el acta de cualquier negocio. Ahora exige token,
   * y un `<embed src>` no puede mandar cabeceras: se baja por axios —que ya
   * inyecta la sesión en el interceptor— y se muestra el blob.
   *
   * Quien lo llame es responsable de `URL.revokeObjectURL` al desmontar.
   */
  getReceiptPdfObjectUrl: async (
    hourmeterId: number,
    operatorId: number,
    role: 'operator' | 'supervisor' | 'resident' = 'operator'
  ): Promise<string> => {
    const response = await api.get(
      `/hourmeters/${hourmeterId}/pdf?operatorId=${operatorId}&role=${role}&download=false`,
      { responseType: 'blob' }
    );
    const blob = new Blob([response.data], { type: 'application/pdf' });
    return URL.createObjectURL(blob);
  },

  // Sign hourmeter
  signHourmeter: async (data: SignatureRequest, role: 'operator' | 'supervisor' | 'resident' = 'operator'): Promise<SignatureResponse> => {
    const formData = new FormData();
    formData.append('operatorId', data.operatorId.toString());
    formData.append('residentVerified', data.residentVerified.toString());
    formData.append('signature', data.signature);
    
    if (data.email) {
      formData.append('email', data.email);
    }
    
    if (data.observations) {
      formData.append('observations', data.observations);
    }

    const response = await api.post(`/hourmeters/${data.hourmeterId}/sign`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
        'role': role,
      },
    });
    return response.data;
  },

  // Reject hourmeter
  rejectHourmeter: async (data: RejectHourmeterRequest): Promise<RejectHourmeterResponse> => {
    const response = await api.post<RejectHourmeterResponse>(
      `/hourmeters/${data.hourmeterId}/reject`,
      {
        operatorId: data.operatorId,
        comments: data.comments,
        residentVerified: data.residentVerified,
        role: data.role
      }
    );
    return response.data;
  },

  // Update hourmeter (maintain reject history)
  updateHourmeter: async (
    hourmeterId: number, 
    operatorId: number, 
    updates: Partial<HourmeterUpdateData>
  ): Promise<HourmeterUpdateResponse> => {
    const response = await api.put(`/hourmeters/${hourmeterId}/update`, {
      operatorId,
      ...updates
    });
    return response.data;
  },

  createHourmeterOperator: async (
    data: CreateHourmeterOperatorRequest,
    images: File[] = []
  ): Promise<CreateHourmeterOperatorResponse> => {
    const formData = new FormData();
    formData.append('dealId', data.dealId.toString());
    formData.append('operatorId', data.operatorId.toString());
    if (data.residentId !== undefined) {
      formData.append('residentId', data.residentId.toString());
    }
    formData.append('initialHour', data.initialHour.toString());
    formData.append('finalHour', data.finalHour.toString());
    formData.append('date', data.date);
    formData.append('standby', data.standby.toString());

    if(data.quantity) {
      formData.append('quantity', data.quantity.toString());
    }

    if (data.accesorio !== undefined) {
      formData.append('accesorio', data.accesorio.toString());
    }

    if (data.horasTrabajoAccesorio !== undefined) {
      formData.append('horasTrabajoAccesorio', data.horasTrabajoAccesorio.toString());
    }

    if (data.startDate) {
      formData.append('startDate', data.startDate);
    }

    if (data.finalDate) {
      formData.append('finalDate', data.finalDate);
    }

    if (data.operatorObservations) {
      formData.append('operatorObservations', data.operatorObservations);
    }

    if (data.novelty) {
      formData.append('novelty', data.novelty);
    }

    images.forEach((file) => {
      formData.append('signedDocImage', file);
    });

    const response = await api.post('/createHourmeterOperator', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });

    return response.data;
  },

  getActiveDealForOperator: async (operatorId: number): Promise<ActiveDealInfo | null> => {
    const response = await api.get(`/getDealActiveOperator/${operatorId}`);
    return response.data;
  },

  // RS-1338: contexto activo unificado (DEAL legacy o OPERATION SaaS).
  getActiveContext: async (operatorId: number): Promise<import('../types').ActiveContext> => {
    const response = await api.get(`/active-context/${operatorId}`);
    return response.data;
  },

  sendReminderToResident: async (operatorId: number, residentId: number): Promise<{ success: boolean; message: string }> => {
    const response = await api.post('/send-reminder-to-resident', {
      operatorId,
      residentId,
    });
    return response.data;
  },
};

export const discountService = {
  createFuelDiscount: async (
    data: CreateFuelDiscountRequest,
    imageFile: File
  ): Promise<FuelDiscountResponse> => {
    const formData = new FormData();
    formData.append('operatorId', data.operatorId.toString());
    formData.append('dealId', data.dealId.toString());
    formData.append('gallons', data.gallons.toString());
    if (data.observations) formData.append('observations', data.observations);
    if (data.date) formData.append('date', data.date);
    formData.append('file', imageFile);

    const response = await api.post('/fuel-discount', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  },

  getOperatorFuelDiscounts: async (operatorId: number): Promise<FuelDiscountGroup[]> => {
    const response = await api.get(`/fuel-discounts?operatorId=${operatorId}`);
    return response.data;
  },
};

// Resident verification service
export const residentService = {
  // Send verification code to resident
  sendVerificationCode: async (data: SendResidentCodeRequest): Promise<SendResidentCodeResponse> => {
    const response = await api.post<SendResidentCodeResponse>(
      '/supervisor/send-verification',
      data
    );
    return response.data;
  },

  // Verify resident code
  verifyCode: async (data: VerifyResidentCodeRequest): Promise<VerifyResidentCodeResponse> => {
    const response = await api.post<VerifyResidentCodeResponse>(
      '/supervisor/verify-code',
      data
    );
    return response.data;
  }
};

export default api;
